import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { IssueReceipt, Receipt, ReceiptStatus, ThermalReceiptResponse } from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS-MONEY — itemised receipts over the typed client, mirroring data/vouchers.ts. Issue a gapless,
 * VAT-split receipt for a succeeded payment, then print it (thermal ESC/POS) or email it. The receipt
 * NUMBER, VAT split, totals and attribution are all the SERVER's — the UI only displays them and never
 * recomputes a total.
 *
 * Issue is IDEMPOTENT (one receipt per payment): a re-issue returns the original receipt with the same
 * number, never a new one. Email enqueues a send job and is idempotent on an optional `idempotencyKey`.
 * Void preserves the gapless number (corrective, never deletes) and is idempotent (voiding a voided
 * receipt returns it unchanged). 422/409 surface faithfully through the shared ApiClientError envelope.
 *
 * Own cache namespace local to this file. The thermal render is fetched ON DEMAND (a print is a user
 * action) via `fetchThermal`, not held in cache — the bytes are produced fresh for each print.
 */
const RECEIPTS_PAGE = 50;

const rk = {
  one: (id: string) => ['receipt', id] as const,
};

export interface ReceiptPage {
  data: Receipt[];
  pagination: { limit: number; offset: number; total: number };
}

/** The receipt-history filter (WS6 back-office): a window + status + optional payment. */
export interface ReceiptsFilter {
  paymentId?: string;
  from?: string;
  to?: string;
  status?: ReceiptStatus;
  offset?: number;
}

// ── Reads ──────────────────────────────────────────────────────────────────────────

/**
 * List receipts in a window (RLS scopes to your book / the floor). Accepts EITHER the legacy
 * `(paymentId?, offset?)` call (kept working for the till) OR a {paymentId, from, to, status, offset}
 * filter object for the back-office receipt-history surface, which forwards the window/status filters
 * the API supports. Every figure on each receipt is the server's — the UI only displays them.
 */
export function useReceipts(filter?: ReceiptsFilter | string, offset = 0) {
  const f: ReceiptsFilter =
    typeof filter === 'string' || filter === undefined
      ? { paymentId: filter, offset }
      : { offset: 0, ...filter };
  const query = {
    paymentId: f.paymentId,
    from: f.from,
    to: f.to,
    status: f.status,
    limit: RECEIPTS_PAGE,
    offset: f.offset ?? 0,
  };
  return useQuery({
    queryKey: qk.receipts(query),
    queryFn: async (): Promise<ReceiptPage> =>
      unwrap(await api.GET('/v1/receipts', { params: { query } })),
    placeholderData: keepPreviousData,
  });
}

/** One receipt with its line items, VAT breakdown and split attribution (the till detail view). */
export function useReceipt(id: string | null) {
  return useQuery({
    queryKey: id ? rk.one(id) : ['receipt', 'none'],
    enabled: Boolean(id),
    queryFn: async (): Promise<Receipt> =>
      unwrap(await api.GET('/v1/receipts/{id}', { params: { path: { id: id! } } })),
  });
}

// ── Mutations ────────────────────────────────────────────────────────────────────────

/**
 * Issue an itemised receipt for a succeeded payment (gapless number, VAT split, attribution). One
 * receipt per payment: a re-issue returns the original (same number). Lines are derived server-side
 * from the payment + splits when `lineItems` is omitted. Caches the result under its id so a Print/
 * Email straight after issue reads it without a round-trip.
 */
export function useIssueReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: IssueReceipt): Promise<Receipt> =>
      unwrap(await api.POST('/v1/receipts', { body })),
    onSuccess: (receipt) => {
      qc.setQueryData(rk.one(receipt.id), receipt);
      void qc.invalidateQueries({ queryKey: ['receipts'] });
    },
  });
}

/**
 * (Re)send the receipt by email — enqueues a send job. `to` defaults server-side to the receipt's
 * client email; pass it to override. Idempotent on an optional `idempotencyKey` minted once per send.
 */
export function useEmailReceipt() {
  return useMutation({
    mutationFn: async (input: {
      id: string;
      to?: string;
      idempotencyKey?: string;
    }): Promise<{ queued: boolean }> =>
      unwrap(
        await api.POST('/v1/receipts/{id}/email', {
          params: { path: { id: input.id } },
          body: { to: input.to, idempotencyKey: input.idempotencyKey },
        }),
      ),
  });
}

/**
 * Void an issued receipt (corrective; preserves the gapless number, never deletes). Manager-only on
 * the API; idempotent (voiding a voided receipt returns it unchanged). Updates the cached receipt so
 * the till reflects the voided status without a refetch.
 */
export function useVoidReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; reason?: string }): Promise<Receipt> =>
      unwrap(
        await api.POST('/v1/receipts/{id}/void', {
          params: { path: { id: input.id } },
          body: { reason: input.reason },
        }),
      ),
    onSuccess: (receipt) => {
      qc.setQueryData(rk.one(receipt.id), receipt);
      void qc.invalidateQueries({ queryKey: ['receipts'] });
    },
  });
}

/**
 * Render a receipt as ESC/POS bytes + a monospace preview for a thermal printer. Fetched ON DEMAND
 * (a Print is a user action) rather than as a hook query, so each print produces fresh bytes. The
 * caller drives the printer with `escposBase64` (and shows `lines` as a preview if needed). `width`
 * is the printer's character width; `openDrawer` pops the cash drawer on print.
 */
export async function fetchThermalReceipt(
  id: string,
  opts: { width?: 32 | 42 | 48; openDrawer?: boolean } = {},
): Promise<ThermalReceiptResponse> {
  return unwrap(
    await api.GET('/v1/receipts/{id}/thermal', {
      params: {
        path: { id },
        query: {
          width: opts.width,
          openDrawer: opts.openDrawer ? 'true' : 'false',
        },
      },
    }),
  );
}
